/**
 * Zustand-based Auth Store (Web implementation)
 * Matches the mobile app's authentication state management pattern
 * Adapted from fmc-digital-mobile-app/api/auth/src/store/useUserStore.ts
 */

import { create } from "zustand";
import { tokenStorage } from "../storage";
import type { UserInfo, AuthTokens, MFAFactor } from "../types";

export type AuthStoreState = {
  /** Whether the user is authenticated */
  authenticated: boolean;
  /** Whether logout was triggered by session expiry */
  sessionLogout: boolean;
  /** Current MFA state token */
  stateToken: string;
  /** State token expiry (Unix ms) */
  stateTokenExpiresAt?: number;
  /** Immutable login MFA anchor from initial /authn (Unix ms) */
  loginMfaAnchorExpiresAt?: number;
  /** Session token from Okta */
  sessionToken: string;
  /** Available MFA factors */
  mfaFactors: MFAFactor[];
  /** Current user info */
  user: UserInfo | null;
  /** Currently selected MFA factor */
  selectedFactor: MFAFactor | null;
  /** MFA factors grouped by type */
  factorsByGroup: Record<string, MFAFactor>;
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: string | null;
  /** Auth tokens */
  tokens: AuthTokens | null;
};

export type AuthStoreActions = {
  /** Set authenticated state */
  setAuthenticated: (authenticated: boolean) => void;
  /** Set logged in state with user data */
  setLoggedIn: (payload: {
    user: UserInfo;
    sessionToken?: string;
    tokens?: AuthTokens;
  }) => void;
  /** Set MFA factors and state */
  setMfaFactors: (payload: {
    factors?: MFAFactor[];
    stateToken?: string;
    stateTokenExpiresAt?: number;
    loginMfaAnchorExpiresAt?: number;
  }) => void;
  /** Select a new MFA factor */
  setNewSelectedFactor: (factorId: string) => void;
  /** Clear MFA state without resetting authenticated user state */
  clearMfaState: () => void;
  /** Logout user */
  logout: () => Promise<void>;
  /** Reset session logout flag */
  resetSessionLogout: () => void;
  /** Reset user to initial state */
  resetUser: () => void;
  /** Set loading state */
  setLoading: (isLoading: boolean) => void;
  /** Set error */
  setError: (error: string | null) => void;
  /** Set tokens */
  setTokens: (tokens: AuthTokens) => void;
};

const initialUser: UserInfo | null = null;

export const initialAuthState: AuthStoreState = {
  authenticated: false,
  sessionLogout: false,
  stateToken: "",
  stateTokenExpiresAt: undefined,
  loginMfaAnchorExpiresAt: undefined,
  sessionToken: "",
  mfaFactors: [],
  user: initialUser,
  selectedFactor: null,
  factorsByGroup: {},
  isLoading: false,
  error: null,
  tokens: null,
};

/**
 * Group MFA factors by type for easy lookup
 */
export const groupFactorsByType = (
  factors: MFAFactor[]
): Record<string, MFAFactor> => {
  return factors.reduce((acc, curr) => {
    return Object.assign(acc, { [curr.factorType]: curr });
  }, {} as Record<string, MFAFactor>);
};

/**
 * Get the default MFA factor (selected one or first available)
 */
export const getDefaultFactor = (
  mfaFactors: MFAFactor[]
): MFAFactor | null => {
  if (mfaFactors.length === 0) return null;
  
  // Find a factor marked as selected, or default to first
  const selectedFactor = mfaFactors.find((factor: MFAFactor) => 
    (factor as any).selected === true
  );
  
  return selectedFactor ?? mfaFactors[0];
};

/**
 * Main auth store using Zustand
 */
export const useAuthStore = create<AuthStoreState & AuthStoreActions>(
  (set, get) => ({
    ...initialAuthState,

    setAuthenticated: (authenticated: boolean) => set({ authenticated }),

    setLoggedIn: (payload: {
      user: UserInfo;
      sessionToken?: string;
      tokens?: AuthTokens;
    }) =>
      set({
        authenticated: true,
        user: payload.user,
        sessionToken: payload.sessionToken ?? "",
        tokens: payload.tokens ?? null,
        stateToken: "",
        stateTokenExpiresAt: undefined,
        loginMfaAnchorExpiresAt: undefined,
        mfaFactors: [],
        selectedFactor: null,
        factorsByGroup: {},
        isLoading: false,
        error: null,
      }),

    setMfaFactors: (payload: {
      factors?: MFAFactor[];
      stateToken?: string;
      stateTokenExpiresAt?: number;
      loginMfaAnchorExpiresAt?: number;
    }) => {
      const current = get();
      const factors = payload.factors ?? current.mfaFactors;
      set({
        mfaFactors: factors,
        stateToken: payload.stateToken ?? current.stateToken,
        stateTokenExpiresAt:
          payload.stateTokenExpiresAt ?? current.stateTokenExpiresAt,
        loginMfaAnchorExpiresAt:
          payload.loginMfaAnchorExpiresAt ?? current.loginMfaAnchorExpiresAt,
        sessionToken: "",
        selectedFactor: getDefaultFactor(factors),
        factorsByGroup: groupFactorsByType(factors),
        isLoading: false,
      });
    },

    setNewSelectedFactor: (factorId: string) => {
      const { mfaFactors } = get();
      const factor = mfaFactors.find((f) => f.id === factorId);
      if (!factor) {
        const availableIds = mfaFactors.map(f => f.id).join(', ');
        const errorMsg = `MFA factor with ID "${factorId}" not found. Available factors: [${availableIds ?? 'none'}]`;
        console.error(errorMsg);
        throw new Error(errorMsg);
      }
      set({ selectedFactor: factor });
    },

    clearMfaState: () =>
      set({
        stateToken: "",
        stateTokenExpiresAt: undefined,
        loginMfaAnchorExpiresAt: undefined,
        mfaFactors: [],
        selectedFactor: null,
        factorsByGroup: {},
      }),

    logout: async () => {
      try {
        // Clear tokens from storage
        await tokenStorage.clearTokens();
      } catch (error) {
        console.error("Error during logout:", error);
      }
      set({
        ...initialAuthState,
        sessionLogout: true,
      });
    },

    resetSessionLogout: () => set({ sessionLogout: false }),

    resetUser: () => set(initialAuthState),

    setLoading: (isLoading: boolean) => set({ isLoading }),

    setError: (error: string | null) => set({ error }),

    setTokens: (tokens: AuthTokens) => set({ tokens }),
  })
);

// Convenience selectors (matching mobile app pattern)

/** Hook to get authenticated state */
export const useAuthenticated = () =>
  useAuthStore((state) => state.authenticated);

/** Hook to get current user */
export const useUser = () => useAuthStore((state) => state.user);

/** Hook to get session logout flag */
export const useSessionLogout = () =>
  useAuthStore((state) => state.sessionLogout);

/** Hook to get MFA factors */
export const useMfaFactors = () => useAuthStore((state) => state.mfaFactors);

/** Hook to get selected MFA factor */
export const useSelectedFactor = () =>
  useAuthStore((state) => state.selectedFactor);

/** Hook to get loading state */
export const useAuthLoading = () => useAuthStore((state) => state.isLoading);

/** Hook to get error state */
export const useAuthError = () => useAuthStore((state) => state.error);

/** Hook to get auth tokens */
export const useAuthTokens = () => useAuthStore((state) => state.tokens);

/** Hook to get full auth state */
export const useAuthState = () => useAuthStore((state) => ({
  authenticated: state.authenticated,
  user: state.user,
  isLoading: state.isLoading,
  error: state.error,
  tokens: state.tokens,
  mfaFactors: state.mfaFactors,
  selectedFactor: state.selectedFactor,
  stateToken: state.stateToken,
}));
